import { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ArrowDown } from "lucide-react";
import HeroScene from "@/components/HeroScene";
import { PROFILE } from "@/data/content";

const line1 = PROFILE.name.split(""); // PIYUSH
const line2 = PROFILE.last.split(""); // SONI

const letterVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.4 + i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const [finePointer, setFinePointer] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(pointer: fine)");
    setFinePointer(mq.matches);
    const on = (e) => setFinePointer(e.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  // Cursor-magnetic name
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const shiftX = useSpring(useTransform(mx, [-0.5, 0.5], [-26, 26]), { stiffness: 120, damping: 18 });
  const shiftY = useSpring(useTransform(my, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 18 });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    if (!finePointer) return;
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={finePointer ? onMove : undefined}
      onMouseLeave={finePointer ? onLeave : undefined}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[560px] w-full overflow-hidden"
      style={{ perspective: 1200 }}
    >
      {/* Generative Three.js network sphere */}
      <div className="absolute inset-0 z-0">
        <HeroScene />
      </div>

      {/* Contrast layer behind text for legibility */}
      <div
        className="pointer-events-none absolute inset-0 z-10"
        style={{
          background:
            "radial-gradient(60% 45% at 50% 52%, rgba(5,5,5,0.72) 0%, rgba(5,5,5,0.35) 45%, rgba(5,5,5,0) 75%)",
        }}
      />

      {/* Kinetic name — PIYUSH SONI */}
      <motion.div
        style={{ y: textY, x: shiftX, translateY: shiftY, rotateY: rotY, transformStyle: "preserve-3d" }}
        className="absolute inset-0 z-20 flex flex-col items-center justify-center"
      >
        <h1 aria-label={PROFILE.fullName} className="flex flex-col items-center leading-[0.8]">
          <span className="font-display flex text-[17vw] md:text-[15vw] text-[#f2f0e6]">
            {line1.map((ch, i) => (
              <span key={i} className="reveal-mask">
                <motion.span custom={i} variants={letterVariants} initial="hidden" animate="show" className="inline-block">
                  {ch}
                </motion.span>
              </span>
            ))}
          </span>
          <span className="font-display flex text-[17vw] md:text-[15vw] text-stroke-emerald -mt-[2vw]">
            {line2.map((ch, i) => (
              <span key={i} className="reveal-mask">
                <motion.span custom={i + line1.length} variants={letterVariants} initial="hidden" animate="show" className="inline-block">
                  {ch}
                </motion.span>
              </span>
            ))}
          </span>
        </h1>
      </motion.div>

      {/* Subtitle + scroll cue */}
      <motion.div
        style={{ opacity: fade }}
        className="absolute inset-x-0 bottom-10 z-30 flex flex-col items-center px-6 text-center"
      >
        <div className="reveal-mask">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-subtitle"
            className="max-w-xl text-sm md:text-lg font-medium tracking-tight text-[#f2f0e6]"
          >
            BCA student building <span className="text-[#10b981]">AI-powered SaaS</span> products.
          </motion.p>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.35em] text-[#7c7a72]"
        >
          <ArrowDown className="h-3.5 w-3.5 animate-bounce" strokeWidth={2} />
          Scroll
        </motion.div>
      </motion.div>

      {/* Top-left marker */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute left-6 top-24 z-30 hidden text-[11px] uppercase tracking-[0.3em] text-[#7c7a72] md:block"
      >
        Portfolio — 2026
        <br />
        <span className="text-[#b9b6a8]">India</span>
      </motion.div>
    </section>
  );
}
